import type { CSSProperties } from "react";
import Image from "next/image";
import clsx from "clsx";

type FloatingIconCSSVars = CSSProperties & {
  "--floating-icon-glow"?: string;
  "--balloon-float-duration"?: string;
  "--balloon-float-delay"?: string;
  "--balloon-float-distance"?: string;
};

export type FloatingIconProps = {
  /** Public-relative image path (e.g. an `AI_MODEL_BRANDS` entry's `src`). */
  src: string;
  alt: string;
  /** Intrinsic dimensions of the source image (aspect ratio only). */
  width: number;
  height: number;
  /**
   * Visual treatment:
   * - `prominent` (default): brand-glow pedestal + ground reflection, for
   *   hero/section use.
   * - `faint`: ghosted monochrome logo with no pedestal, for background decor.
   */
  intensity?: "prominent" | "faint";
  /** Rendered box size in px (square). Defaults to 132 (96 for `faint`). */
  size?: number;
  /** Brand glow tint behind the icon (`prominent` only). */
  glow?: string;
  /** Whether the icon drifts up and down like a balloon. */
  float?: boolean;
  /** Float travel distance in px. Defaults to 14. */
  distance?: number;
  /** Seconds for one full up/down cycle. Defaults to 5.5. */
  duration?: number;
  /**
   * Animation delay in seconds. Use negative values to desync a row of icons
   * without delaying their first appearance.
   */
  delay?: number;
  /** Elliptical ground reflection under the icon. Defaults to `prominent`. */
  reflection?: boolean;
  /** Forwarded to next/image for above-the-fold icons. */
  priority?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * A brand/model icon that floats gently like a balloon over a glowing pedestal.
 *
 * Defaults reproduce the Studio "One subscription. The best AI models." section;
 * pass `intensity="faint"` for the bottom-CTA background treatment, or tune
 * `glow`/`size`/`distance`/`duration`/`delay` for other placements. The float
 * animation is automatically disabled under `prefers-reduced-motion`.
 */
export function FloatingIcon({
  src,
  alt,
  width,
  height,
  intensity = "prominent",
  size,
  glow,
  float = true,
  distance,
  duration,
  delay,
  reflection,
  priority = false,
  className,
  style,
}: Readonly<FloatingIconProps>) {
  const isFaint = intensity === "faint";
  const boxSize = size ?? (isFaint ? 96 : 132);
  const showReflection = reflection ?? !isFaint;

  const cssVars: FloatingIconCSSVars = {
    width: boxSize,
    height: boxSize,
    ...(glow ? { "--floating-icon-glow": glow } : {}),
    ...(distance !== undefined
      ? { "--balloon-float-distance": `${distance}px` }
      : {}),
    ...(duration !== undefined
      ? { "--balloon-float-duration": `${duration}s` }
      : {}),
    ...(delay !== undefined ? { "--balloon-float-delay": `${delay}s` } : {}),
    ...style,
  };

  return (
    <span
      className={clsx(
        "floating-icon",
        isFaint && "floating-icon--faint",
        showReflection && "floating-icon--reflection",
        className,
      )}
      style={cssVars}
    >
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        // The icon always renders at the fixed box size, so cap the candidate
        // Next.js serves rather than letting it pick from the 512px intrinsic.
        sizes={`${boxSize}px`}
        priority={priority}
        className={clsx("floating-icon__media", float && "animate-balloon-float")}
      />
    </span>
  );
}
